import type { KambiCouponValidateFailResponse } from '@mobi/api-types/kambi/player/coupon'
import type { KambiValidReward } from '@mobi/api-types/kambi/player/reward'
import type { KambiSportsSelection } from '@mobi/betslip/types'
import {
  ConfirmBet,
  ConfirmKambiBetFailed,
  ConfirmKambiBetSucceeded,
  ProposeBet,
  ProposeKambiBetFailed,
  ProposeKambiBetSucceeded,
} from '@core/Areas/Quickbet/signals'
import { postPlaceKambiSportsBet, postValidateKambiSportsBet } from '@core/Data/Betting/kambiSports'
import { isApiServiceError } from '@core/Services/ApiService'
import { fetchAccountBalanceAsync } from '@core/State/UserAccount/async-signals'
import { createKambiBetRequest } from './utils/createBetRequest'
import { handleResponseErrors } from './utils/handleResponseErrors'

/**
 * Handle kambi bet validation, checks for price change, betting closed etc
 * */
export const proposeKambiBetAsync = async ({ selection }: ProposeParams) => {
  ProposeBet()
  const request = createKambiBetRequest({ selection, investmentWin: 1 })

  try {
    const response = await postValidateKambiSportsBet(request)

    if (response.status === 'SUCCESS') {
      ProposeKambiBetSucceeded()
      return
    }

    throw Error()
  } catch (error) {
    if (error instanceof Error && isApiServiceError(error)) {
      const response: KambiCouponValidateFailResponse = await error.response.json()
      handleResponseErrors({
        requestType: 'propose',
        request,
        response,
        responseHttpCode: error.response.status,
      })
    } else {
      ProposeKambiBetFailed()
    }
  }
}

/**
 * Handle kambi bet placement
 * */
export const confirmKambiBetAsync = async ({
  selection,
  investmentWin,
  selectedKambiReward,
  copiedBetLoopPostId,
}: ConfirmParams) => {
  ConfirmBet()

  const baseRequest = createKambiBetRequest({ selection, selectedKambiReward, investmentWin })

  const request = copiedBetLoopPostId ? { ...baseRequest, copiedBetLoopPostId } : baseRequest

  try {
    const response = await postPlaceKambiSportsBet(request)

    if (response.status === 'SUCCESS') {
      fetchAccountBalanceAsync()
      ConfirmKambiBetSucceeded({ coupon: response.coupon })
      return
    }

    throw Error()
  } catch (error) {
    if (error instanceof Error && isApiServiceError(error)) {
      const response: KambiCouponValidateFailResponse = await error.response.json()
      handleResponseErrors({
        requestType: 'commit',
        request,
        response,
        responseHttpCode: error.response.status,
      })
    } else {
      ConfirmKambiBetFailed()
    }
  }
}

// =====
// Types
// =====

type ProposeParams = {
  selection: KambiSportsSelection
}

type ConfirmParams = {
  selection: KambiSportsSelection
  selectedKambiReward: KambiValidReward | null
  investmentWin: number
  copiedBetLoopPostId?: string
}
